import React, { useState } from 'react';
import { useBranches } from '../../context/branches';
import { useCustomers } from '../../context/customers';
import { useDrivers } from '../../context/drivers';
import { useTripStatuses, useTripTypes } from "../../context/trips";
import { useVehicles } from '../../context/vehicles';
import { XMarkIcon } from "@heroicons/react/24/solid";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { USER_ROLE } from '../../services/auth';


const TripHeader = ({ 
  inputSearch,
  count,
  showFilters,
  onSearch,
  onShowFilters,
  handleAdd,
  trip,
  showForm,
  onSearchSubmit,
  onClearSearch,
  onStatusUpdate,
  onBack,
  readOnly
}) => {

  if (showForm) {
    return (
      <div className="flex justify-between flex-wrap gap-2 mb-4">
        <h1 className="text-xl font-bold uppercase text-[#293686]">
          {trip
            ? readOnly
              ? `View Trip (${trip.trip_code})`
              : `Edit Trip (${trip.trip_code})`
            : "Add Trip"}
        </h1>
        <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
          { !readOnly && trip && ["created"].includes(trip.status) && (
            <button onClick={() => onStatusUpdate(trip, "ongoing", true)} className="bg-green-600 text-white px-3 py-1 rounded text-sm">
              {"Mark as Ongoing"}
            </button>
          )}
          { !readOnly && trip && ["ongoing"].includes(trip.status) && (
            <button onClick={() => onStatusUpdate(trip, "completed", true)} className="bg-green-600 text-white px-3 py-1 rounded text-sm">
              {"Complete Trip"}
            </button>
          )}
          { !readOnly && trip && ["created"].includes(trip.status) && (
            <button onClick={() => onStatusUpdate(trip, "cancelled", true)} className="bg-red-600 text-white px-3 py-1 rounded text-sm">
              {"Cancel Trip"}
            </button>
          )}
          <button onClick={onBack} className="bg-blue-600 text-white px-3 py-1 rounded text-sm">
            {"< Go Back"}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-between flex-wrap gap-2 mb-4">
      <h1 className="text-xl font-bold uppercase text-[#293686]">Trips({count})</h1>
      <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
        <form onSubmit={onSearchSubmit} className="relative w-full md:w-72">
          <input
            type="text"
            placeholder="Search by Code, Driver, Vehicle"
            value={inputSearch}
            onChange={onSearch}
            className="border rounded w-full pl-3 pr-10 py-1.5 text-sm"
          />
          {/* Clear (X) button — appears only when there's text */}
          {inputSearch && (
            <button
              type="button"
              onClick={onClearSearch}
              className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Clear"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            title="Search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
              />
            </svg>
          </button>
        </form>
        <button
          onClick={onShowFilters}
          className="border border-gray-400 px-3 py-1 rounded text-sm"
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>

        <button onClick={handleAdd} className="bg-blue-600 text-white px-3 py-1 rounded text-sm">
          + Add Trip
        </button>
      </div>
    </div>
  );
};

export default TripHeader;

export const TripFilters = ({
  filters,
  onFilterChange,
}) => {

  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role === 'admin');

  let branches = [], branch_loading = false;
  if (showBranches) {
    const result = useBranches({ reload: false, params: { status: 'active', paginate: false } });
    branches = result.branches;
    branch_loading = result.loading;
  }

  const { customers, loading: customer_loading, refresh: refreshCustomers } = useCustomers({ reload: false, params: { paginate: false } });
  const { drivers, loading: driver_loading, refresh: refreshDrivers } = useDrivers({ reload: false, params: { status: 'active', paginate: false } });
  const { tripStatuses: statuses, loading: trip_status_loading, refresh: refreshTripStatuses } = useTripStatuses({ reload: false, params: { status: 'active', paginate: false } });
  const { tripTypes, loading: triptype_loading, refresh: refreshTripTypes } = useTripTypes({ reload: false });
  const { vehicles, loading: vehicle_loading, refresh: refreshVehicles } = useVehicles({ reload: false, params: { status: 'active', paginate: false } });

  const [selectedBranch, setSelectedBranch] = useState(0);

  const filteredDrivers = (showBranches && selectedBranch && selectedBranch != 0) ?
    drivers.filter(d => d.branch.id === Number(selectedBranch)) :
    drivers;

  const filteredVehicles = (showBranches && selectedBranch && selectedBranch != 0) ?
    vehicles.filter(v => v.branch.id === Number(selectedBranch)) :
    vehicles;

  const onBranchChange = (e) => {
    setSelectedBranch(e.target.value);
    onFilterChange(e, 'branch');
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
      {/* First row */}
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Trip Type</label>
        <select
          value={filters.type}
          onChange={e => onFilterChange(e, 'type')}
          className="border px-2 py-1 text-sm rounded"
        >
          <option value="">All</option>
          {tripTypes.map(tt => (
            <option key={tt.id} value={tt.id}>
              {tt.value}
            </option>
          ))}
        </select>
      </div>
      {showBranches && (
        <div className="flex flex-col">
          <label className="text-xs text-gray-600 mb-0.5">Branch</label>
          <select
            value={filters.branch}
            onChange={onBranchChange}
            className="border px-2 py-1 text-sm rounded"
          >
            <option value={0}>All</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Requested Vehicle</label>
        <select
          value={filters.requested_vehicle}
          onChange={e => onFilterChange(e, 'requested_vehicle')}
          className="border px-2 py-1 text-sm rounded"
        >
          <option value="">All</option>
          {filteredVehicles.map(v => (
            <option key={v.id} value={v.id}>
              {v.vehicle_number}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Assigned Vehicle</label>
        <select
          value={filters.assigned_vehicle}
          onChange={e => onFilterChange(e, 'assigned_vehicle')}
          className="border px-2 py-1 text-sm rounded"
        >
          <option value="">All</option>
          {filteredVehicles.map(v => (
            <option key={v.id} value={v.id}>
              {v.vehicle_number}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Driver</label>
        <select
          value={filters.driver}
          onChange={e => onFilterChange(e, 'driver')}
          className="border px-2 py-1 text-sm rounded"
        >
          <option value="">All</option>
          {filteredDrivers.map(d => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      {/* Second row - Date filters */}
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Trip Status</label>
        <select
          value={filters.status}
          onChange={e => onFilterChange(e, 'status')}
          className="border px-2 py-1 text-sm rounded"
        >
          <option value="">All</option>
          {statuses.map(s => (
            <option key={s.id} value={s.id}>
              {s.value}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Customer</label>
        <select
          value={filters.customer}
          onChange={e => onFilterChange(e, 'customer')}
          className="border px-2 py-1 text-sm rounded"
        >
          <option value="">All</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Created Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.created_at}
            onChange={e => onFilterChange(e, 'created_at')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.created_at && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "created_at"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Booking Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.booking_date}
            onChange={e => onFilterChange(e, 'booking_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.booking_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "booking_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Placement Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.placement_pickup_date}
            onChange={e => onFilterChange(e, 'placement_pickup_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.placement_pickup_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "placement_pickup_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Expected Pickup Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.expected_pickup_date}
            onChange={e => onFilterChange(e, 'expected_pickup_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.expected_pickup_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "expected_pickup_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Pickup In Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.actual_in_pickup_date}
            onChange={e => onFilterChange(e, 'actual_in_pickup_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.actual_in_pickup_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "actual_in_pickup_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Pickup Out Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.actual_out_pickup_date}
            onChange={e => onFilterChange(e, 'actual_out_pickup_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.actual_out_pickup_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "actual_out_pickup_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Expected Delivery Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.expected_delivery_date}
            onChange={e => onFilterChange(e, 'expected_delivery_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.expected_delivery_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "expected_delivery_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Delivery In Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.actual_in_delivery_date}
            onChange={e => onFilterChange(e, 'actual_in_delivery_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.actual_in_delivery_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "actual_in_delivery_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col">
        <label className="text-xs text-gray-600 mb-0.5">Delivery Out Date</label>
        <div className="relative inline-block">
          <input
            type="date"
            value={filters.actual_out_delivery_date}
            onChange={e => onFilterChange(e, 'actual_out_delivery_date')}
            className="border px-2 py-1 text-sm rounded w-full"
          />
          {filters.actual_out_delivery_date && (
            <XMarkIcon
              onClick={() =>
                onFilterChange(
                  { target: { value: "" } },
                  "actual_out_delivery_date"
                )
              }
              className="absolute right-7 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
              style={{ width: '16px', height: '16px' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
